/**
 * SpatialControlManager — KNN Grid Spatial Control Heatmap Visualization.
 * Ported 1:1 from app/templates/components/visualization/spatial-control.js.html
 * (805 lines) for React migration Pass 11, keeping its imperative API.
 *
 * Renders spatial control heatmaps from parquet files onto the pitch canvas.
 * Uses matplotlib seismic colormap (blue → white → red) with gamma contrast.
 *
 * The heavy binary decode runs in the EXISTING static Web Worker,
 * app/static/js/spatial-control-worker.js, loaded same-origin at
 * /static/js/spatial-control-worker.js — deliberately NOT ported: the file is
 * unchanged production code served by Flask, and both the Jinja page and this
 * React page can share it (message contract: {type:'decode'|'decode_json'}
 * in, {type:'progress'|'complete'|'error'} out).
 *
 * PARITY NOTE: the template's toggle() read a hidden legacy checkbox
 * (#layer-spatial-control, display:none combobox) to learn the desired state;
 * the React page has no hidden checkbox, so toggle() takes the desired
 * enabled state explicitly. Everything downstream is unchanged.
 */

import { computeControlGrid, type SpatialPlayerLike } from './spatialCompute'

export interface SpatialFrameEntry {
  frame: number
  team0_control_pct?: number
  team1_control_pct?: number
  control_grid: Float32Array | string // Float32Array (binary/worker) or base64 (JSON legacy)
}

/** The slice of the pitch canvasState this manager reads. */
export interface SpatialCanvasStateView {
  ctx: CanvasRenderingContext2D | null
  canvas: HTMLCanvasElement | null
  frames: { frame: number }[]
  currentFrameIndex: number
}

interface SpatialControlOptions {
  getGameCode: () => string | null
  getCanvasState: () => SpatialCanvasStateView
  onRedraw: (frameData: { frame: number }) => void
  /** The pitch rectangle in canvas pixels — the controller owns it. */
  getFieldRect: () => { x: number; y: number; w: number; h: number }
  /** Player positions for a frame from the tracking cache, or null if that
   *  frame isn't cached. When provided (and computeLocal is on), grids are
   *  computed here (~0.2 ms) instead of fetched as 24 MB windows. */
  getTrackingFrame?: (frame: number) => { players: SpatialPlayerLike[] } | null
  /** Ask the tracking cache to load the chunk containing `frame`. */
  ensureTrackingFrame?: (frame: number) => Promise<void>
}

interface WorkerCompleteFrame {
  frame: number
  team0_control_pct: number
  team1_control_pct: number
  control_grid: Float32Array
}

interface WorkerMessage {
  type: 'progress' | 'complete' | 'error'
  frames?: WorkerCompleteFrame[]
  metadata?: { gridHeight: number; gridWidth: number; frameInterval: number }
  decoded?: number
  total?: number
  error?: string
}

interface RGB {
  r: number
  g: number
  b: number
}

export class SpatialControlManager {
  getGameCode: () => string | null
  getCanvasState: () => SpatialCanvasStateView
  onRedraw: (frameData: { frame: number }) => void
  getFieldRect: () => { x: number; y: number; w: number; h: number }
  getTrackingFrame?: (frame: number) => { players: SpatialPlayerLike[] } | null
  ensureTrackingFrame?: (frame: number) => Promise<void>

  // Runtime state
  enabled = false
  data = new Map<number, SpatialFrameEntry>() // frame number -> frame data
  cache = new Map<number, Float32Array>() // frame -> decoded grids
  renderedCache = new Map<number, HTMLCanvasElement>() // frame -> pre-rendered
  /** True while ANY window fetch is in flight. Speculative preloads defer to
   *  it; demand loads (seek, toggle, ensureLoaded) deliberately do not. */
  loading = false
  /** In-flight load per window start frame. Dedupes concurrent requests for
   *  the same window without serializing requests for different ones. */
  inflightLoads = new Map<number, Promise<boolean>>()
  error: string | null = null
  loadedWindows = new Set<number>()
  lastLoadRequest = 0

  // Configuration
  config = {
    // Grid settings
    gridShape: [69, 106], // [height, width] - matches Python
    fieldMargin: 40, // margin around field (must match drawField)

    // Window/cache settings
    windowSize: 4500, // ACTUAL frames per window (reduced from 9000 to fit Cloud Run response limits ~26MB)
    maxCacheSize: 3000, // max decoded grids (increased for bulk loading)
    maxRenderedCache: 300, // max rendered canvases
    maxWindows: 10, // max windows in memory (increased to maintain frame coverage with smaller windows)
    frameInterval: 5, // spatial control every N frames
    preloadThreshold: 1500, // preload when N ACTUAL frames from edge (50 sec at 30fps)
    loadThrottle: 500, // min ms between loads

    // Rendering settings (clinical style - Octavian-style)
    blurRadius: 6, // canvas blur in pixels
    gamma: 0.9, // contrast: < 1 = wider white zone, > 1 = more saturated
    alpha: 0.85, // heatmap opacity (0-1)

    // Data source
    useLocalSource: false, // true = local parquet, false = R2
    useBinaryFormat: true, // true = binary format, false = JSON (legacy)
    /** Compute grids in the browser from tracking data (spatialCompute.ts)
     *  instead of fetching precomputed windows. Falls back to window fetch
     *  per frame whenever tracking data isn't available (e.g. the JSON
     *  tracking fallback, or a chunk that failed to load). */
    computeLocal: true,

    // Debug
    debug: false, // verbose logging
  }

  // Web Worker for decoding
  worker: Worker | null = null
  workerReady = false
  /** Single slot: the resolver for the ONE decode currently in the worker.
   *  The worker's complete/error messages carry no request id, so this slot
   *  must never belong to two decodes at once — _decodeInWorker chains them. */
  workerResolve: ((ok: boolean) => void) | null = null
  private decodeQueue: Promise<unknown> = Promise.resolve()
  _colors: { blue: RGB; blueDeep: RGB; red: RGB; redDeep: RGB } | null = null

  constructor(options: SpatialControlOptions) {
    this.getGameCode = options.getGameCode
    this.getCanvasState = options.getCanvasState
    this.onRedraw = options.onRedraw
    this.getFieldRect = options.getFieldRect
    this.getTrackingFrame = options.getTrackingFrame
    this.ensureTrackingFrame = options.ensureTrackingFrame
    this._initWorker()
  }

  /** Snap a frame to the sampling cadence shared with the server data. */
  private _snap(frameNumber: number): number {
    const iv = this.config.frameInterval
    return Math.round(frameNumber / iv) * iv
  }

  /** Sample frames whose grids came from local compute (vs a fetched
   *  window). Draw uses this to skip window preloads only for frames the
   *  compute path owns — window-served frames must keep preloading. */
  private computedFrames = new Set<number>()

  /**
   * Compute-and-cache the grid for one sampled frame from local tracking
   * data. Returns true only when the COMPUTE path serves this frame; a
   * frame already present from a fetched window returns false so the
   * window machinery (preloads, eviction) keeps owning it.
   */
  private _tryComputeFrame(sampleFrame: number): boolean {
    if (!this.config.computeLocal || !this.getTrackingFrame) return false
    if (this.computedFrames.has(sampleFrame)) return true
    if (this.data.has(sampleFrame)) return false // window data owns it
    const tf = this.getTrackingFrame(sampleFrame)
    if (!tf || !tf.players || tf.players.length === 0) return false
    const computed = computeControlGrid(tf.players)
    if (!computed) return false // <2 teams — the pipeline skips these too
    this.data.set(sampleFrame, {
      frame: sampleFrame,
      team0_control_pct: computed.team0Pct,
      team1_control_pct: computed.team1Pct,
      control_grid: computed.grid,
    })
    this.cache.set(sampleFrame, computed.grid)
    this.computedFrames.add(sampleFrame)
    this._evictComputedEntries(sampleFrame)
    return true
  }

  /** Bound memory for computed frames. Window-loaded entries are evicted by
   *  _evictOldWindows, but computed entries belong to no window, so they get
   *  the same distance rule as the decoded-grid cache. */
  private _evictComputedEntries(currentFrame: number): void {
    if (this.computedFrames.size <= this.config.maxCacheSize) return
    const keepRange = this.config.windowSize * 2
    for (const frame of this.computedFrames) {
      if (Math.abs(frame - currentFrame) > keepRange) {
        this.data.delete(frame)
        this.cache.delete(frame)
        this.computedFrames.delete(frame)
      }
    }
  }

  /** Initialize Web Worker for background decoding */
  private _initWorker(): void {
    try {
      // The existing static worker, same-origin, unchanged (see file header).
      this.worker = new Worker('/static/js/spatial-control-worker.js')
      this.worker.onmessage = (e) => this._handleWorkerMessage(e)
      this.worker.onerror = (error) => {
        console.error('Spatial Control Worker error:', error)
        this.workerReady = false
      }
      this.workerReady = true
      this._log('Web Worker initialized')
    } catch (error) {
      console.warn('Failed to initialize Web Worker, falling back to main thread:', error)
      this.workerReady = false
    }
  }

  /** Handle messages from Web Worker */
  private _handleWorkerMessage(e: MessageEvent<WorkerMessage>): void {
    const { type, frames, metadata, decoded, total, error } = e.data

    switch (type) {
      case 'progress':
        this._log(`Decoding progress: ${decoded}/${total} frames`)
        break
      case 'complete':
        this._log(`Decoded ${frames!.length} frames`)
        // Store decoded frames
        frames!.forEach((frame) => {
          this.data.set(frame.frame, frame)
          this.cache.set(frame.frame, frame.control_grid)
        })
        // Update metadata
        if (metadata) {
          this.config.gridShape = [metadata.gridHeight, metadata.gridWidth]
          this.config.frameInterval = metadata.frameInterval
        }

        // Resolve the promise if one is waiting
        if (this.workerResolve) {
          this.workerResolve(true)
          this.workerResolve = null
        }
        break
      case 'error':
        console.error('Worker decode error:', error)
        this.error = error || 'decode error'

        // Reject the promise if one is waiting
        if (this.workerResolve) {
          this.workerResolve(false)
          this.workerResolve = null
        }
        break
    }
  }

  /** Enable or disable spatial control overlay */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled
    this._log(`Spatial Control: ${enabled ? 'ON' : 'OFF'}`)
  }

  /** Check if spatial control is enabled */
  isEnabled(): boolean {
    return this.enabled
  }

  /**
   * Toggle spatial control and load data if needed.
   * (Template read the hidden #layer-spatial-control checkbox here; the React
   * caller passes the desired state instead — see file header.)
   */
  async toggle(enabled: boolean): Promise<void> {
    this.enabled = enabled

    this._log(`Spatial Control: ${this.enabled ? 'ON' : 'OFF'}`)

    // When enabling, make the current frame's data available: computed from
    // tracking when possible, else the correct window.
    const canvasState = this.getCanvasState()
    if (this.enabled && canvasState.frames?.length > 0) {
      const currentFrame = canvasState.frames[canvasState.currentFrameIndex]?.frame || 0
      try {
        await this.ensureLoaded(currentFrame)
      } catch {
        // ensureLoaded already logged; the overlay stays absent for now
      }
    }

    // Redraw current frame
    const current = canvasState.frames?.[canvasState.currentFrameIndex]
    if (current) this.onRedraw(current)
  }

  /** Load spatial control data for a specific frame */
  async loadData(frameNumber: number = 0): Promise<boolean> {
    // Compute mode needs no bulk loading — grids appear on demand.
    if (this._tryComputeFrame(this._snap(frameNumber))) return true

    const windowStart = this._getWindowStartFrame(frameNumber)

    if (this.loadedWindows.has(windowStart)) {
      return true
    }

    const framesPerWindow = this.config.windowSize
    this._log(`Loading window ${windowStart}-${windowStart + framesPerWindow - 1}...`)

    const success = await this._loadWindow(windowStart)

    if (success) {
      // Preload next window — speculative, so it yields to any load already
      // in flight rather than competing with it for bandwidth.
      const nextWindowStart = windowStart + framesPerWindow
      setTimeout(() => {
        if (!this.loading) void this._loadWindow(nextWindowStart)
      }, 100)
    }

    return success
  }

  /**
   * Ensure spatial control data is loaded for a specific frame.
   * Resolves when data is ready (or immediately if already loaded or disabled).
   */
  async ensureLoaded(frameNumber: number): Promise<void> {
    // If not enabled, resolve immediately (no data needed)
    if (!this.enabled) {
      return Promise.resolve()
    }

    // Compute path: serve from local tracking data when available. If the
    // tracking chunk isn't cached yet, ask for it (the same chunk the
    // playhead needs anyway) and try once more; only then fall back to a
    // window fetch, so a game without browser tracking still gets data.
    if (this.config.computeLocal && this.getTrackingFrame) {
      const snapped = this._snap(frameNumber)
      if (this._tryComputeFrame(snapped)) return
      if (this.ensureTrackingFrame) {
        try {
          await this.ensureTrackingFrame(snapped)
        } catch {
          // tracking unavailable — fall through to the window path
        }
        if (this._tryComputeFrame(snapped)) return
      }
      this._log(`ensureLoaded: no tracking data for ${snapped}, using window fetch`)
    }

    const windowStart = this._getWindowStartFrame(frameNumber)

    // If window already loaded, resolve immediately
    if (this.loadedWindows.has(windowStart)) {
      this._log(`ensureLoaded: Window ${windowStart} already loaded`)
      return Promise.resolve()
    }

    // Load the window and wait for it to complete
    this._log(`ensureLoaded: Loading window ${windowStart} for frame ${frameNumber}`)
    const success = await this._loadWindow(windowStart)

    if (success) {
      this._log(`ensureLoaded: Successfully loaded window ${windowStart}`)
      return Promise.resolve()
    } else {
      console.error(`ensureLoaded: Failed to load window ${windowStart}`)
      return Promise.reject(
        new Error(`Failed to load spatial control data for frame ${frameNumber}`),
      )
    }
  }

  /** Draw spatial control heatmap on canvas */
  draw(frameNumber: number): void {
    if (!this.enabled || !this.data) return

    const canvasState = this.getCanvasState()
    const ctx = canvasState.ctx
    const canvas = canvasState.canvas
    if (!ctx || !canvas) return

    // Compute path first: an exact grid for this sample frame beats any
    // window bookkeeping, and takes ~0.2 ms when the tracking chunk is here.
    const snapped = this._snap(frameNumber)
    const computeServed = this._tryComputeFrame(snapped)
    const exactAvailable = computeServed || this.data.has(snapped)

    // CRITICAL: Without an exact entry for this sample frame, the correct
    // window must be loaded — this prevents drawing stale data from old
    // windows (the _findClosestFrame fallback scan can reach far entries).
    const windowStart = this._getWindowStartFrame(frameNumber)
    if (!exactAvailable && !this.loadedWindows.has(windowStart)) {
      this._log(`Window ${windowStart} not loaded for frame ${frameNumber} - skipping draw to avoid stale data`)
      return
    }

    // Preload check — pointless only when local compute owns this frame;
    // window-served frames keep their preload behavior (follow-ups #19).
    if (!computeServed) this._checkAndPreload(frameNumber)

    // The pitch rectangle, from the one place that owns it.
    const rect = this.getFieldRect()
    const fieldX = rect.x
    const fieldY = rect.y
    const fieldWidth = rect.w
    const fieldHeight = rect.h

    if (this.data.size === 0) return

    // Snap to nearest sampled frame (O(1) instead of linear scan).
    // Spatial data is sampled every frameInterval frames, so the closest
    // entry is always at a predictable key.
    const closestFrameNumber = this._findClosestFrame(frameNumber)

    if (closestFrameNumber === null) return

    // Warn on frame mismatch (only when debug enabled)
    const frameDelta = Math.abs(frameNumber - closestFrameNumber)
    if (this.config.debug && frameDelta > 5) {
      console.warn(
        `[SpatialControl] Frame mismatch: canvas=${frameNumber}, spatial=${closestFrameNumber}, delta=${frameDelta} frames`,
      )
    }

    // FAST PATH: Use cached canvas
    if (this.renderedCache.has(closestFrameNumber)) {
      const cachedCanvas = this.renderedCache.get(closestFrameNumber)!
      ctx.save()
      ctx.imageSmoothingEnabled = true
      ctx.imageSmoothingQuality = 'high'
      ctx.globalAlpha = this.config.alpha
      ctx.filter = `blur(${this.config.blurRadius}px)`
      ctx.drawImage(cachedCanvas, fieldX, fieldY, fieldWidth, fieldHeight)
      ctx.restore()
      return
    }

    // SLOW PATH: Render and cache
    this._log(`Cache MISS for frame ${closestFrameNumber} - rendering`)
    const grid = this._getDecodedGrid(closestFrameNumber)
    if (!grid) {
      this._log(`No grid data for frame ${closestFrameNumber}`)
      return
    }

    // Grid dimensions
    const GRID_X = 106 // pitch length
    const GRID_Y = 69 // pitch width
    const canvasWidth = GRID_X
    const canvasHeight = GRID_Y

    // Create ImageData
    const imageData = new ImageData(canvasWidth, canvasHeight)
    for (let row = 0; row < canvasHeight; row++) {
      for (let col = 0; col < canvasWidth; col++) {
        const gridX = col
        const gridY = canvasHeight - 1 - row // Flip Y
        const gridIdx = gridX * GRID_Y + gridY
        // A Float32Array read in range is always a number; 0 is what an
        // untouched cell holds, so the fallback is that cell's true value.
        const value = grid[gridIdx] ?? 0
        const color = this._getSeismicColor(value)

        const idx = (row * canvasWidth + col) * 4
        imageData.data[idx] = color.r
        imageData.data[idx + 1] = color.g
        imageData.data[idx + 2] = color.b
        imageData.data[idx + 3] = color.a
      }
    }

    // Create and cache heatmap canvas
    const heatmapCanvas = document.createElement('canvas')
    heatmapCanvas.width = canvasWidth
    heatmapCanvas.height = canvasHeight
    heatmapCanvas.getContext('2d')!.putImageData(imageData, 0, 0)

    this.renderedCache.set(closestFrameNumber, heatmapCanvas)
    this._log(`Cached frame ${closestFrameNumber}, cache size: ${this.renderedCache.size}`)

    // Evict if needed
    if (this.renderedCache.size > this.config.maxRenderedCache) {
      this._evictRenderedCache(frameNumber)
    }

    // Draw with blur and alpha
    ctx.save()
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.globalAlpha = this.config.alpha
    ctx.filter = `blur(${this.config.blurRadius}px)`
    ctx.drawImage(heatmapCanvas, fieldX, fieldY, fieldWidth, fieldHeight)
    ctx.restore()
  }

  /** Terminate the worker and drop caches (React page unmount). */
  destroy(): void {
    if (this.worker) {
      this.worker.terminate()
      this.worker = null
      this.workerReady = false
    }
    // A decode mid-flight will never hear back from a terminated worker;
    // release its waiter so queued loads resolve (false) instead of hanging.
    if (this.workerResolve) {
      this.workerResolve(false)
      this.workerResolve = null
    }
    this.data.clear()
    this.cache.clear()
    this.renderedCache.clear()
    this.loadedWindows.clear()
    this.computedFrames.clear()
  }

  // =========================================================================
  // PRIVATE METHODS
  // =========================================================================

  private _log(message: string): void {
    if (this.config.debug) {
      console.log(`[SpatialControl] ${message}`)
    }
  }

  /**
   * O(1) lookup: snap a frame number to the nearest sampled key in this.data.
   * Spatial data is sampled every frameInterval frames, so the closest entry
   * is at a predictable arithmetic position. Falls back to neighbors if the
   * exact snap is absent (e.g. near window boundaries).
   */
  private _findClosestFrame(frameNumber: number): number | null {
    const iv = this.config.frameInterval
    const snapped = Math.round(frameNumber / iv) * iv
    if (this.data.has(snapped)) return snapped
    // Try immediate neighbors (covers rounding at window edges)
    if (this.data.has(snapped - iv)) return snapped - iv
    if (this.data.has(snapped + iv)) return snapped + iv
    // Rare path: keys not aligned to the interval (a server-side sampling
    // offset would do it). One linear scan beats a blank overlay.
    let closest: number | null = null
    let minDiff = Infinity
    for (const frame of this.data.keys()) {
      const diff = Math.abs(frame - frameNumber)
      if (diff < minDiff) {
        minDiff = diff
        closest = frame
      }
    }
    return closest
  }

  private _getWindowStartFrame(frameNumber: number): number {
    // windowSize is already in ACTUAL frames (not spatial samples)
    // API expects window_size in actual frames, so no need to multiply by frameInterval
    const framesPerWindow = this.config.windowSize
    return Math.floor(frameNumber / framesPerWindow) * framesPerWindow
  }

  /**
   * Load one window, deduped PER WINDOW rather than serialized globally.
   *
   * The old single-slot design made every caller wait out whatever load was
   * already in flight — so a backward seek sat behind an irrelevant ~26 MB
   * forward-preload before the overlay could appear (follow-ups #19A). Now a
   * demand load starts its fetch immediately; only two callers asking for the
   * SAME window share a promise. The concurrency stays in the network:
   * decode handoff to the worker is serialized in _decodeInWorker.
   */
  private async _loadWindow(startFrame: number): Promise<boolean> {
    if (this.loadedWindows.has(startFrame)) {
      this._log(`Window ${startFrame} already loaded`)
      return true
    }

    const inflight = this.inflightLoads.get(startFrame)
    if (inflight) {
      this._log(`Window ${startFrame} already loading — joining in-flight request`)
      return inflight
    }

    this._log(`Loading window ${startFrame}`)
    this.loading = true
    this.lastLoadRequest = Date.now()

    const load = this._executeLoad(startFrame).finally(() => {
      this.inflightLoads.delete(startFrame)
      this.loading = this.inflightLoads.size > 0
    })
    this.inflightLoads.set(startFrame, load)
    return load
  }

  /**
   * Hand one payload to the worker; resolves when the worker reports back.
   *
   * Fetches may overlap, decodes must not: the worker's complete/error
   * messages carry no request id, so the single workerResolve slot has to
   * belong to exactly one decode at a time. Chaining on decodeQueue keeps
   * that true without giving up concurrent downloads — and the worker
   * processes messages sequentially anyway, so nothing is lost.
   */
  private _decodeInWorker(message: { type: 'decode' | 'decode_json'; data: unknown }): Promise<boolean> {
    const run = this.decodeQueue.then(() => {
      // The manager may have been destroyed while this decode sat queued.
      if (!this.worker || !this.workerReady) return false
      return new Promise<boolean>((resolve) => {
        this.workerResolve = resolve
        this.worker!.postMessage(message)
      })
    })
    this.decodeQueue = run.catch(() => false)
    return run
  }

  /** Execute the actual window load (extracted for promise tracking) */
  private async _executeLoad(startFrame: number): Promise<boolean> {
    const framesPerWindow = this.config.windowSize
    const endFrame = startFrame + framesPerWindow - 1

    try {
      const gameCode = this.getGameCode()
      const sourceParam = this.config.useLocalSource ? '&source=local' : ''
      const formatParam = this.config.useBinaryFormat ? '&format=binary' : ''
      const url = `/api/v1/game-match-summary/spatial-control/${gameCode}?start_frame=${startFrame}&window_size=${framesPerWindow}${sourceParam}${formatParam}`

      const startTime = Date.now()
      const response = await fetch(url, { credentials: 'same-origin' })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }

      const loadTime = Date.now() - startTime

      // Binary format - decode in Web Worker
      if (this.config.useBinaryFormat) {
        const arrayBuffer = await response.arrayBuffer()

        this._log(`Loaded ${arrayBuffer.byteLength} bytes in ${loadTime}ms, decoding...`)

        if (this.workerReady && this.worker) {
          // Decode in worker (serialized — see _decodeInWorker)
          const success = await this._decodeInWorker({ type: 'decode', data: arrayBuffer })
          this.loadedWindows.add(startFrame)

          // Evict old windows
          const canvasState = this.getCanvasState()
          const currentFrame =
            canvasState.frames?.[canvasState.currentFrameIndex]?.frame || startFrame
          this._evictOldWindows(currentFrame)

          return success
        } else {
          // Fallback: decode on main thread (synchronous)
          this._decodeBinaryDataMainThread(arrayBuffer)
          this.loadedWindows.add(startFrame)

          // Evict old windows
          const canvasState = this.getCanvasState()
          const currentFrame =
            canvasState.frames?.[canvasState.currentFrameIndex]?.frame || startFrame
          this._evictOldWindows(currentFrame)

          return true
        }
      }
      // JSON format (legacy)
      else {
        const data = await response.json()

        if (data.source === 'local') {
          this._log('Using LOCAL parquet files')
        }

        if (data.success) {
          if (this.workerReady && this.worker) {
            // Decode in worker (serialized — see _decodeInWorker)
            const success = await this._decodeInWorker({ type: 'decode_json', data })
            this.loadedWindows.add(startFrame)

            // Evict old windows
            const canvasState = this.getCanvasState()
            const currentFrame =
              canvasState.frames?.[canvasState.currentFrameIndex]?.frame || startFrame
            this._evictOldWindows(currentFrame)

            this._log(`Window ${startFrame}-${endFrame}: ${data.frames.length} frames in ${loadTime}ms`)
            return success
          } else {
            // Store frames directly (already base64 decoded by browser)
            data.frames.forEach((frame: SpatialFrameEntry) => {
              this.data.set(frame.frame, frame)
            })

            // Update config from metadata
            this.config.gridShape = data.metadata.grid_dimensions
            this.config.frameInterval = data.metadata.frame_interval

            this.loadedWindows.add(startFrame)

            // Evict old windows
            const canvasState = this.getCanvasState()
            const currentFrame =
              canvasState.frames?.[canvasState.currentFrameIndex]?.frame || startFrame
            this._evictOldWindows(currentFrame)

            this._log(`Window ${startFrame}-${endFrame}: ${data.frames.length} frames in ${loadTime}ms`)
            return true
          }
        } else {
          this.error = data.error || 'Failed to load'
          console.warn('Spatial control not available:', this.error)
          return false
        }
      }
    } catch (error) {
      console.error('Error loading spatial control:', error)
      this.error = 'Data not available'
      return false
    }
  }

  /** Decode binary data on main thread (fallback if worker unavailable) */
  private _decodeBinaryDataMainThread(arrayBuffer: ArrayBuffer): void {
    const view = new DataView(arrayBuffer)
    let offset = 0

    // Read header
    const frameCount = view.getUint32(offset, true)
    offset += 4
    const gridHeight = view.getUint32(offset, true)
    offset += 4
    const gridWidth = view.getUint32(offset, true)
    offset += 4
    const frameInterval = view.getUint32(offset, true)
    offset += 4
    offset += 4 // startFrame (unused, kept in the stream)
    offset += 4 // endFrame (unused, kept in the stream)

    const gridSize = gridHeight * gridWidth
    this.config.gridShape = [gridHeight, gridWidth]
    this.config.frameInterval = frameInterval

    // Read frames
    for (let i = 0; i < frameCount; i++) {
      const frameNumber = view.getInt32(offset, true)
      offset += 4
      const team0ControlPct = view.getFloat32(offset, true)
      offset += 4
      const team1ControlPct = view.getFloat32(offset, true)
      offset += 4

      const gridData = new Float32Array(gridSize)
      for (let j = 0; j < gridSize; j++) {
        gridData[j] = view.getFloat32(offset, true)
        offset += 4
      }

      this.data.set(frameNumber, {
        frame: frameNumber,
        team0_control_pct: team0ControlPct,
        team1_control_pct: team1ControlPct,
        control_grid: gridData,
      })
      this.cache.set(frameNumber, gridData)
    }

    this._log(`Decoded ${frameCount} frames on main thread`)
  }

  /** Speculative preloads. The `!this.loading` gates are the preload half of
   *  the demand/preload split: a preload never starts while anything else is
   *  downloading, while demand loads (toggle/ensureLoaded/auto-load) start
   *  regardless of what is in flight. */
  private _checkAndPreload(frameNumber: number): void {
    if (!this.enabled) return

    const windowStart = this._getWindowStartFrame(frameNumber)
    const framesPerWindow = this.config.windowSize
    const windowEnd = windowStart + framesPerWindow - 1
    const threshold = this.config.preloadThreshold // Already in actual frames

    // Preload next window
    if (frameNumber > windowEnd - threshold) {
      const next = windowStart + framesPerWindow
      if (!this.loadedWindows.has(next) && !this.loading) {
        this._log(`Preloading next window ${next}`)
        void this._loadWindow(next)
      }
    }

    // Preload previous window
    if (frameNumber < windowStart + threshold) {
      const prev = Math.max(0, windowStart - framesPerWindow)
      if (!this.loadedWindows.has(prev) && !this.loading) {
        this._log(`Preloading previous window ${prev}`)
        void this._loadWindow(prev)
      }
    }
  }

  _getDecodedGrid(frameNumber: number): Float32Array | null {
    if (this.data.size === 0) return null

    // Exact hit (computed frames live outside any window): serve directly,
    // never trigger a window auto-load for data we already hold.
    const exact = this.cache.get(frameNumber)
    if (exact) return exact
    const exactEntry = this.data.get(frameNumber)
    if (exactEntry && exactEntry.control_grid instanceof Float32Array) {
      this.cache.set(frameNumber, exactEntry.control_grid)
      return exactEntry.control_grid
    }

    const windowStart = this._getWindowStartFrame(frameNumber)
    if (!this.loadedWindows.has(windowStart)) {
      // Auto-load if needed. This is a DEMAND load — the playhead is inside
      // this window right now — so it only defers to an in-flight load of the
      // same window (via the throttle), never to a preload of some other one.
      const now = Date.now()
      if (now - this.lastLoadRequest > this.config.loadThrottle && !this.inflightLoads.has(windowStart)) {
        this._log(`Auto-loading window for frame ${frameNumber}`)
        void this._loadWindow(windowStart).then((success) => {
          if (success) {
            const canvasState = this.getCanvasState()
            const currentFrame = canvasState.frames?.[canvasState.currentFrameIndex]?.frame
            const framesPerWindow = this.config.windowSize
            if (
              currentFrame !== undefined &&
              currentFrame >= windowStart &&
              currentFrame <= windowStart + framesPerWindow - 1
            ) {
              this._log(`Auto-load complete, redrawing`)
              const redraw = canvasState.frames?.[canvasState.currentFrameIndex]
              if (redraw) this.onRedraw(redraw)
            }
          }
        })
      }
      return null
    }

    this._checkAndPreload(frameNumber)

    // Snap to nearest sampled frame (O(1) — same as draw()).
    const closest = this._findClosestFrame(frameNumber)
    if (closest === null) return null
    const closestData = this.data.get(closest)
    if (!closestData) return null

    // Check decoded cache
    if (this.cache.has(closest)) {
      return this.cache.get(closest)!
    }

    // Already-decoded grid (worker/binary path stores Float32Array directly)
    if (closestData.control_grid instanceof Float32Array) {
      this.cache.set(closest, closestData.control_grid)
      this._evictOldCacheEntries(frameNumber)
      return closestData.control_grid
    }

    // Decode base64 (JSON legacy path)
    const binaryString = atob(closestData.control_grid)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const grid = new Float32Array(bytes.buffer)

    this.cache.set(closest, grid)
    this._evictOldCacheEntries(frameNumber)

    return grid
  }

  private _getSeismicColor(value: number): { r: number; g: number; b: number; a: number } {
    // Read colors from design-tokens.css (cached after first read)
    if (!this._colors) {
      const style = getComputedStyle(document.documentElement)
      // A CSS variable can be missing or malformed — it is a stylesheet, not
      // a typed value — so a component that does not parse falls back to 0
      // rather than making the whole color NaN.
      const parseRgb = (varName: string): RGB => {
        const [r, g, b] = style
          .getPropertyValue(varName)
          .trim()
          .split(',')
          .map((n) => parseInt(n.trim()))
        return { r: r ?? 0, g: g ?? 0, b: b ?? 0 }
      }
      this._colors = {
        blue: parseRgb('--spatial-control-blue-rgb'),
        blueDeep: parseRgb('--spatial-control-blue-deep-rgb'),
        red: parseRgb('--spatial-control-red-rgb'),
        redDeep: parseRgb('--spatial-control-red-deep-rgb'),
      }
    }
    const { blue, blueDeep, red, redDeep } = this._colors

    value = Math.max(-1, Math.min(1, value))

    // Invert sign to match player dot colors:
    // Backend: Team 0 → positive, Team 1 → negative
    // Player dots: Team 0 → blue, Team 1 → red
    // So we negate to get: Team 0 → negative → blue, Team 1 → positive → red
    value = -value

    // Gamma contrast enhancement
    const sign = value >= 0 ? 1 : -1
    value = sign * Math.pow(Math.abs(value), this.config.gamma)

    // Thresholds for color zones (higher = wider deep color center)
    const blueDeepThreshold = 0.35 // Blue: wider dark center
    const redDeepThreshold = 0.5 // Red: original threshold

    let r: number, g: number, b: number
    if (value < -blueDeepThreshold) {
      // Deep blue zone (-1 to -blueDeepThreshold) - Team 0 strong control
      const t = (value + 1) / (1 - blueDeepThreshold)
      r = Math.floor(blueDeep.r + (blue.r - blueDeep.r) * t)
      g = Math.floor(blueDeep.g + (blue.g - blueDeep.g) * t)
      b = Math.floor(blueDeep.b + (blue.b - blueDeep.b) * t)
    } else if (value < 0) {
      // Blue to white (-blueDeepThreshold to 0) - Team 0 fading
      const t = (value + blueDeepThreshold) / blueDeepThreshold
      r = Math.floor(blue.r + (255 - blue.r) * t)
      g = Math.floor(blue.g + (255 - blue.g) * t)
      b = Math.floor(blue.b + (255 - blue.b) * t)
    } else if (value < redDeepThreshold) {
      // White to red (0 to redDeepThreshold) - Team 1 emerging
      const t = value / redDeepThreshold
      r = Math.floor(255 - (255 - red.r) * t)
      g = Math.floor(255 - (255 - red.g) * t)
      b = Math.floor(255 - (255 - red.b) * t)
    } else {
      // Deep red zone (redDeepThreshold to 1) - Team 1 strong control
      const t = (value - redDeepThreshold) / (1 - redDeepThreshold)
      r = Math.floor(red.r - (red.r - redDeep.r) * t)
      g = Math.floor(red.g - (red.g - redDeep.g) * t)
      b = Math.floor(red.b - (red.b - redDeep.b) * t)
    }

    return { r, g, b, a: 255 }
  }

  private _evictOldCacheEntries(currentFrame: number): void {
    if (this.cache.size <= this.config.maxCacheSize) return

    const framesPerWindow = this.config.windowSize
    const keepRange = framesPerWindow * 2

    for (const frame of this.cache.keys()) {
      if (Math.abs(frame - currentFrame) > keepRange) {
        this.cache.delete(frame)
      }
    }
    this._log(`Cache eviction: ${this.cache.size} entries remaining`)
  }

  private _evictOldWindows(currentFrame: number): void {
    if (this.loadedWindows.size <= this.config.maxWindows) return

    const currentWindow = this._getWindowStartFrame(currentFrame)
    const framesPerWindow = this.config.windowSize
    const keepDistance = Math.floor(this.config.maxWindows / 2)

    const windowsToKeep = new Set<number>()
    for (let i = -keepDistance; i <= keepDistance; i++) {
      const ws = currentWindow + i * framesPerWindow
      if (ws >= 0) windowsToKeep.add(ws)
    }

    const toRemove: number[] = []
    for (const ws of this.loadedWindows) {
      if (!windowsToKeep.has(ws)) toRemove.push(ws)
    }

    for (const ws of toRemove) {
      const windowEnd = ws + framesPerWindow - 1
      for (const [frame] of this.data.entries()) {
        if (frame >= ws && frame <= windowEnd) {
          this.data.delete(frame)
        }
      }
      this.loadedWindows.delete(ws)
    }

    if (toRemove.length > 0) {
      this._log(`Window eviction: removed ${toRemove.length}, ${this.loadedWindows.size} remaining`)
    }
  }

  private _evictRenderedCache(currentFrame: number): void {
    if (this.renderedCache.size <= this.config.maxRenderedCache) return

    const keepRange = 2000
    let evicted = 0

    for (const frame of this.renderedCache.keys()) {
      if (Math.abs(frame - currentFrame) > keepRange) {
        this.renderedCache.delete(frame)
        evicted++
        if (this.renderedCache.size <= this.config.maxRenderedCache) break
      }
    }

    if (evicted > 0) {
      this._log(`Rendered cache eviction: removed ${evicted}, ${this.renderedCache.size} remaining`)
    }
  }
}
